import { AUTHENTICATION_ACTIONS, telemetry } from 'utils/telemetry';
import { AUTH_API_DOMAIN } from 'constants/api';
import CookieUtils from 'utils/cookies';
import { HTTPError } from 'utils/HTTPError';
import { OauthAccessTokenData } from './auth';

// Types for cross-tab communication
export enum TokenRefreshMessageTypeEnum {
  TOKEN_REFRESH_STARTED = 'TOKEN_REFRESH_STARTED',
  TOKEN_REFRESH_SUCCESS = 'TOKEN_REFRESH_SUCCESS',
  TOKEN_REFRESH_ERROR = 'TOKEN_REFRESH_ERROR',
  TOKEN_REFRESH_STATE_UPDATE = 'TOKEN_REFRESH_STATE_UPDATE',
}

interface TokenRefreshMessage {
  type: TokenRefreshMessageTypeEnum;
  tokenData?: OauthAccessTokenData;
  error?: string;
  tabId: string;
  isRefreshInProgress?: boolean;
}

interface CrossTabTokenRefreshOptions {
  lockName?: string;
  channelName?: string;
  lockTimeout?: number;
}

class CrossTabTokenRefresh {
  private lockName: string;
  private channelName: string;
  private lockTimeout: number;
  private broadcastChannel: BroadcastChannel | null = null;
  private currentRefreshPromise: Promise<OauthAccessTokenData> | null = null;
  private messageHandlers: Set<(message: TokenRefreshMessage) => void> = new Set();
  private tabId: string;
  private crossTabRefreshState: Map<string, boolean> = new Map(); // tabId -> isRefreshInProgress

  constructor(options: CrossTabTokenRefreshOptions = {}) {
    this.lockName = options.lockName || `${process.env.APP_ENV}-admin-token-refresh-lock`;
    this.channelName = options.channelName || `${process.env.APP_ENV}-admin-token-refresh-channel`;
    this.lockTimeout = options.lockTimeout || 10_000; // 10 seconds
    this.tabId = Math.random().toString(36).substring(2, 9);

    this.initializeBroadcastChannel();
  }

  public async refreshAccessToken(): Promise<OauthAccessTokenData> {
    // If there's already a local refresh in progress, return the existing promise
    if (this.currentRefreshPromise) {
      return this.currentRefreshPromise;
    }

    // If there's a cross-tab refresh in progress, wait for it to complete
    if (this.isRefreshInProgress()) {
      telemetry.addAction(AUTHENTICATION_ACTIONS.CROSS_TAB_REFRESH_IN_PROGRESS);
      return this.waitForCrossTabRefresh();
    }

    // Broadcast that refresh is starting
    this.broadcastRefreshStateUpdate(true);

    this.currentRefreshPromise = this.acquireLockAndRefresh();

    // Clean up the promise reference when it completes
    return this.currentRefreshPromise.finally(() => {
      this.clearServiceState();
      this.crossTabRefreshState.delete(this.tabId);
    });
  }

  public isRefreshInProgress(): boolean {
    // Check local refresh state
    const localRefreshInProgress = this.currentRefreshPromise !== null;

    // Check cross-tab refresh state
    const crossTabRefreshInProgress = Array.from(this.crossTabRefreshState.values()).some(
      (isInProgress) => isInProgress
    );

    // Return true if either local or cross-tab refresh is in progress
    return localRefreshInProgress || crossTabRefreshInProgress;
  }

  public destroy(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    this.crossTabRefreshState.clear();
    this.clearServiceState();
  }

  private clearServiceState(): void {
    this.broadcastRefreshStateUpdate(false);
    this.messageHandlers.clear();
    this.currentRefreshPromise = null;
  }

  private initializeBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    try {
      this.broadcastChannel = new BroadcastChannel(this.channelName);
      this.broadcastChannel.onmessage = this.handleBroadcastMessage.bind(this);
    } catch (error) {
      telemetry.addError(error, {
        description: 'Failed to initialize BroadcastChannel',
      });
    }
  }

  private handleBroadcastMessage(event: MessageEvent<TokenRefreshMessage>): void {
    const message = event.data;

    // Ignore messages from the same tab (prevent self-notification)
    if (message.tabId === this.getTabId()) {
      return;
    }

    // Handle refresh state updates
    if (message.type === TokenRefreshMessageTypeEnum.TOKEN_REFRESH_STATE_UPDATE) {
      if (message.isRefreshInProgress !== undefined) {
        this.crossTabRefreshState.set(message.tabId, message.isRefreshInProgress);
      }
    }

    this.messageHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        telemetry.addError(error, {
          description: 'Error in broadcast message handler',
        });
      }
    });
  }

  private getTabId(): string {
    return this.tabId;
  }

  private updateStorageWithTokenInfo(tokenData: OauthAccessTokenData): void {
    CookieUtils.updateHeaders(tokenData);
  }

  private async performActualTokenRefresh(): Promise<OauthAccessTokenData> {
    telemetry.addAction(AUTHENTICATION_ACTIONS.TOKEN_REFRESH_REQUEST);

    const response = await fetch(`${AUTH_API_DOMAIN}/oauth/access_token`, {
      method: 'POST',
      headers: {
        ...CookieUtils.getAuthorizationHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: CookieUtils.get('admin_refresh_token'),
        grant_type: 'refresh_token',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
      }),
    });

    if (!response.ok) {
      throw new HTTPError(response);
    }

    telemetry.addAction(AUTHENTICATION_ACTIONS.TOKEN_REFRESH_SUCCESS);
    const tokenData: OauthAccessTokenData = await response.json();
    this.updateStorageWithTokenInfo(tokenData);

    return tokenData;
  }

  private broadcastMessage(message: Omit<TokenRefreshMessage, 'tabId'>): void {
    if (!this.broadcastChannel) return;

    const fullMessage: TokenRefreshMessage = {
      ...message,
      tabId: this.getTabId(),
    };

    try {
      this.broadcastChannel.postMessage(fullMessage);
    } catch (error) {
      telemetry.addError(error, {
        description: 'Failed to broadcast message',
      });
    }
  }

  private broadcastRefreshStateUpdate(isRefreshInProgress: boolean): void {
    this.broadcastMessage({
      type: TokenRefreshMessageTypeEnum.TOKEN_REFRESH_STATE_UPDATE,
      isRefreshInProgress,
    });
  }

  private async waitForCrossTabRefresh(): Promise<OauthAccessTokenData> {
    return new Promise((resolve, reject) => {
      this.setupFollowerListener(resolve, reject);
    });
  }

  private setupFollowerListener(resolve: (value: OauthAccessTokenData) => void, reject: (reason?: any) => void): void {
    telemetry.addAction(AUTHENTICATION_ACTIONS.CROSS_TAB_TOKEN_REFRESH_FOLLOWER_INITIALIZED);

    const handler = (message: TokenRefreshMessage) => {
      if (message.type === TokenRefreshMessageTypeEnum.TOKEN_REFRESH_SUCCESS && message.tokenData) {
        telemetry.addAction(AUTHENTICATION_ACTIONS.CROSS_TAB_FOLLOWER_RESOLVED);
        resolve(message.tokenData);
        this.messageHandlers.delete(handler);
      } else if (message.type === TokenRefreshMessageTypeEnum.TOKEN_REFRESH_ERROR) {
        telemetry.addAction(AUTHENTICATION_ACTIONS.CROSS_TAB_FOLLOWER_REJECTED);
        reject(new Error(message.error || 'Token refresh failed'));
        this.messageHandlers.delete(handler);
      }
    };

    this.messageHandlers.add(handler);

    // Set up timeout for follower tabs
    setTimeout(() => {
      if (this.messageHandlers.has(handler)) {
        this.messageHandlers.delete(handler);
        reject(new Error('Token refresh timeout - no response from leader tab'));
      }
    }, this.lockTimeout);
  }

  private async acquireLockAndRefresh(): Promise<OauthAccessTokenData> {
    // Check if WebLocks API is available
    if (typeof navigator === 'undefined' || !navigator.locks) {
      return this.performActualTokenRefresh();
    }

    return new Promise((resolve, reject) => {
      navigator.locks.request(this.lockName, { ifAvailable: false, mode: 'exclusive' }, async (lock) => {
        if (lock) {
          telemetry.addAction(AUTHENTICATION_ACTIONS.CROSS_TAB_TOKEN_REFRESH_LEADER_INITIALIZED);
          // This tab acquired the lock - it's the leader
          try {
            this.broadcastMessage({ type: TokenRefreshMessageTypeEnum.TOKEN_REFRESH_STARTED });
            const tokenData = await this.performActualTokenRefresh();
            this.broadcastMessage({
              type: TokenRefreshMessageTypeEnum.TOKEN_REFRESH_SUCCESS,
              tokenData,
            });
            resolve(tokenData);
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.broadcastMessage({
              type: TokenRefreshMessageTypeEnum.TOKEN_REFRESH_ERROR,
              error: errorMessage,
            });
            reject(error);
          }
        } else {
          // This tab couldn't acquire the lock - it's a follower
          this.setupFollowerListener(resolve, reject);
        }
      });
    });
  }
}

export { CrossTabTokenRefresh };
export type { TokenRefreshMessage, CrossTabTokenRefreshOptions };
